import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { PAGINATION_PAGE } from "src/_Globals/urls";
import type { TagContextOptions } from "src/components/Filter/TagFilter/useTagFiltersData";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

export const NETWORK_TAB_PARAM = "tab";

export type NetworkListConfig = {
    sortKeys: Record<string, string>;
    tagContext?: TagContextOptions;
};

export const toNetworkApiSort = (sort: AxiosParams, config: NetworkListConfig): AxiosParams =>
    Object.fromEntries(
        Object.entries(sort).map(([key, direction]) => {
            const columnId = key.replace(/^order\[(.*)\]$/, "$1");
            return [`order[${config.sortKeys[columnId] ?? columnId}]`, direction];
        }),
    );

export const useNetworkListParams = (config: NetworkListConfig) => {
    const [, setSearchParams] = useSearchParams();
    const resetPage = useCallback(() => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete(PAGINATION_PAGE);
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);
    const search = useDelayedSearch({ actionOnDelay: resetPage });
    const {
        filters: urlFilters,
        sort,
        pagination,
    } = useUrlToApi({
        page: "1",
        itemsPerPage: "20",
        tagContext: config.tagContext,
    });

    const filters = useMemo(
        () => Object.fromEntries(Object.entries(urlFilters).filter(([key]) => key !== NETWORK_TAB_PARAM)),
        [urlFilters],
    );
    const apiSort = useMemo(() => toNetworkApiSort(sort, config), [sort, config]);
    const params = useMemo(
        () => ({
            ...filters,
            ...apiSort,
            ...pagination,
            q: search.delayedSearch === "" ? undefined : search.delayedSearch,
        }),
        [filters, apiSort, pagination, search.delayedSearch],
    );

    return { params, filters, apiSort, pagination, search };
};
